import { NotReady } from 'features/verify/features/not-ready';
import { errorType$ } from 'model/error.model';
import { captureErrorFx } from 'model/helpers/analytics.model';
import { log } from 'model/helpers/log';
import { useEffect } from 'preact/hooks';
import { useStore } from 'effector-react';
import { Fragment, FunctionalComponent, h } from 'preact';

import { Logo } from 'ui/logo';
import { ExitPopup } from 'ui/exit-popup';
import { isReady$, readyStatus$ } from 'model/loading.model';
import { routes } from 'features/verify/routes';
import { LanguagePopup } from 'ui/language-popup';
import { $route, navigate, Route } from 'libs/router';
import { mountType$, params$ } from 'model/main.model';
import { Selfie } from 'features/verify/features/selfie';
import { Handoff } from 'features/verify/features/handoff';
import { Welcome } from 'features/verify/features/welcome';
import { ErrorLayout } from 'features/verify/ui/error-layout';
import { Completed } from 'features/verify/features/completed';
import { PhotoError } from 'features/verify/features/error/photo-error';
import { VerificationError } from 'features/verify/features/error';
import { Document } from 'features/verify/features/document/document';
import { VerifyContainer } from 'features/verify/ui/verify-container';
import { PhotoLoading } from 'features/verify/features/photo-loading';
import { ErrorDetails, isIntentParams, MountType } from 'model/domain';
import { AccessRequest } from 'features/verify/features/access-request';
import { AcceptedDocuments } from 'features/verify/features/accepted-documents';
import {
	closeLocalizationPopup,
	isLocalizationPopupOpen$,
} from 'model/localization.model';
import {
	verificationCancel,
	isVerificationExit$,
	closeVerificationExitPopup, isUploading$,
} from 'model/flows/verification.model';

function hasTouch() {
	return 'ontouchstart' in document.documentElement
		|| navigator.maxTouchPoints > 0
		|| navigator.msMaxTouchPoints > 0;
}

export const Verify: FunctionalComponent<{ error?: ErrorDetails }> = ({ error }) => {
	const isReady = useStore(isReady$);
	const isExit = useStore(isVerificationExit$);
	const uploading = useStore(isUploading$);
	const isLocalizationPopupOpen = useStore(isLocalizationPopupOpen$);
	const mountType = useStore(mountType$);
	const params = useStore(params$);
	const route = useStore($route);
	const readyStatus = useStore(readyStatus$);

	useEffect(() => {
		if (error) {
			navigate(routes.error.path);
			return;
		}
		navigate(routes.notReady);
	}, []);

	useEffect(() => {
		if (isReady) {
			navigate(mountType === MountType.verification ?
				routes.welcome :
				routes.handoff,
			);
		}
	}, [isReady]);
	useEffect(() => {
		if (hasTouch()) {
			try {
				for (let si in document.styleSheets) {
					let styleSheet = document.styleSheets[si];
					// @ts-ignore
					if (!styleSheet.rules) {
						continue;
					}

					// @ts-ignore
					for (let ri = styleSheet.rules.length - 1; ri >= 0; ri--) {
						// @ts-ignore
						const item = styleSheet.rules[ri];

						if (!item.selectorText) {
							continue;
						}

						// @ts-ignore
						if (item.selectorText.match(':hover') && item.selectorText.match('hive_')) {
							// @ts-ignore
							styleSheet.deleteRule(ri);
						}
					}
				}
			} catch (ex) {
				captureErrorFx(ex);
			}
		}
	}, []);

	useEffect(() => {
		log({ name: 'verify_ready_status', message: readyStatus });
	}, [readyStatus]);

	const style = {
		top: route === routes.welcome && isIntentParams(params) ? 56 : 0,
	};

	if (error) {
		return (
			<VerifyContainer style={style}>
				{isIntentParams(params) && (
					<Logo
						src={params.options.intent.logo}
					/>
				)}
				<ErrorLayout
					title={error.title}
					description={error.description}
				/>
				<ExitPopup
					overlay={!isIntentParams(params)}
					visible={isExit}
					onClose={() => closeVerificationExitPopup()}
					onExit={() => {
						verificationCancel();
					}}
				/>
			</VerifyContainer>
		);
	} else if (uploading) {
		return (
			<VerifyContainer>
				<PhotoLoading/>
			</VerifyContainer>
		);
	} else {
		return (
			<VerifyContainer style={style}>
				<Route path={routes.notReady} component={<NotReady/>}/>
				{isReady && (
					<Fragment>
						{route === routes.welcome && isIntentParams(params) && (
							<Logo
								src={params.options.intent.logo}
							/>
						)}
						<Route path={routes.handoff} component={<Handoff/>}/>
						<Route path={routes.welcome} component={<Welcome/>}/>
						<Route path={routes.access} component={<AccessRequest/>}/>
						<Route path={routes.complete} component={<Completed/>}/>
						<Route path={routes.selfie} component={<Selfie/>}/>
						<Route path={routes.document} component={<Document/>}/>
						<Route path={routes.photoError} component={<PhotoError/>}/>
						<Route path={routes.error.path} component={<VerificationError/>}/>
						<LanguagePopup
							visible={isLocalizationPopupOpen}
							onClose={() => closeLocalizationPopup()}
						/>
						<ExitPopup
							overlay={!isIntentParams(params)}
							visible={isExit}
							onClose={() => closeVerificationExitPopup()}
							onExit={() => {
								verificationCancel();
							}}
						/>
						<AcceptedDocuments/>
					</Fragment>
				)}
			</VerifyContainer>
		);
	}
};
